/**
 * Providers we frame.
 *
 * YouTube uses the nocookie host: the ordinary one sets tracking cookies for
 * every reader who merely LOADS a page containing the player, which is a
 * consent problem we would be creating for our customers.
 */
export function recogniseEmbed(raw) {
    let url;
    try {
        // GitBook exports wrap the URL in angle brackets inside the attribute.
        url = new URL(raw.trim().replace(/^<|>$/g, ''));
    }
    catch {
        return null;
    }
    if (url.protocol !== 'https:')
        return null;
    const host = url.hostname.replace(/^www\./, '');
    if (host === 'youtube.com' || host === 'm.youtube.com') {
        const id = url.searchParams.get('v');
        if (id && /^[\w-]{6,20}$/.test(id))
            return youtube(id);
    }
    if (host === 'youtu.be') {
        const id = url.pathname.slice(1);
        if (/^[\w-]{6,20}$/.test(id))
            return youtube(id);
    }
    if (host === 'youtube.com' && url.pathname.startsWith('/embed/')) {
        const id = url.pathname.slice('/embed/'.length);
        if (/^[\w-]{6,20}$/.test(id))
            return youtube(id);
    }
    if (host === 'loom.com') {
        // Loom shares as /share/<id>; the embeddable form is /embed/<id>.
        const id = url.pathname.replace(/^\/(share|embed)\//, '');
        if (/^[0-9a-f]{16,}$/i.test(id)) {
            return {
                provider: 'loom',
                src: `https://www.loom.com/embed/${id}`,
                title: 'Loom recording',
                ratio: '16x9',
            };
        }
    }
    if (host === 'vimeo.com') {
        const id = url.pathname.slice(1).split('/')[0];
        if (/^\d{6,}$/.test(id)) {
            return {
                provider: 'vimeo',
                src: `https://player.vimeo.com/video/${id}`,
                title: 'Vimeo video',
                ratio: '16x9',
            };
        }
    }
    if (host === 'app.arcade.software') {
        const id = url.pathname.replace(/^\/share\//, '');
        if (/^[\w-]{6,}$/.test(id)) {
            // Arcade demos are taller than video.
            return {
                provider: 'arcade',
                src: `https://app.arcade.software/share/${id}`,
                title: 'Interactive demo',
                ratio: '16x10',
            };
        }
    }
    /**
     * Figma: a design, a prototype, or a board.
     *
     * Framed through Figma's own embed host, which takes the ORIGINAL file URL
     * as a parameter rather than an id. That is unlike every other provider
     * here, and it is why the file URL is re-encoded rather than pattern-matched
     * for an id: Figma's paths differ by product (`/file/`, `/design/`,
     * `/proto/`, `/board/`) and have changed more than once, so matching the
     * host and letting Figma resolve the rest is the version that does not break
     * when they add the next one.
     *
     * Only the file URL travels, and only after this has satisfied itself that
     * it IS a Figma URL — which is the whole point of an allowlist.
     *
     * NOT verified against a real file. Every Figma URL reachable without an
     * account answers 404, and an error page's `X-Frame-Options` says nothing
     * about an embed's. The host is Figma's own documented embed endpoint and
     * this is the shape every other tool sends it, so it is very probably
     * right — but "very probably" is the honest word, and somebody with a real
     * file should confirm a frame actually paints before this is called done.
     * The gist below WAS confirmed: `.pibb` answers `X-Frame-Options: ALLOWALL`
     * where the gist's own page answers `deny`.
     */
    if (host === 'figma.com') {
        if (/^\/(file|design|proto|board|slides)\/[\w-]{8,}/.test(url.pathname)) {
            return {
                provider: 'figma',
                src: `https://www.figma.com/embed?embed_host=staleless&url=${encodeURIComponent(url.toString())}`,
                title: 'Figma',
                // A design is nearer a page than a video.
                ratio: '16x10',
            };
        }
    }
    /**
     * A GitHub gist, as a real gist rather than a screenshot of one.
     *
     * Gists have no embed host: GitHub's own way is a `<script>` tag, which is
     * exactly what an allowlist exists to refuse — a stranger's JavaScript beside
     * the reader's session, on a page that documents a product. So the frame
     * points at the gist's own page, which GitHub serves in a frame perfectly
     * well and which shows the file, its name and its highlighting.
     *
     * The `.pibb` suffix is deliberate: it is the plain, chrome-free rendering
     * GitHub serves for exactly this, so the reader sees the code rather than a
     * page about the code.
     */
    if (host === 'gist.github.com') {
        const path = url.pathname.replace(/^\/+|\/+$/g, '');
        if (/^[\w-]{1,39}\/[0-9a-f]{20,40}$/.test(path)) {
            return {
                provider: 'gist',
                src: `https://gist.github.com/${path}.pibb`,
                title: 'Code on GitHub',
                ratio: '16x10',
            };
        }
    }
    return null;
}
const youtube = (id) => ({
    provider: 'youtube',
    // nocookie: the ordinary host sets tracking cookies on load, before anyone
    // presses play.
    src: `https://www.youtube-nocookie.com/embed/${id}`,
    title: 'YouTube video',
    ratio: '16x9',
});
//# sourceMappingURL=embeds.js.map